import type {
  CannedResponse,
  FieldOption,
  Form,
  FormKind,
  Page,
  ProjectFieldBinding,
  SearchRequestInput,
  SearchResponse,
  Status,
  TicketListItem,
  WorkflowVersion,
} from '@servicetim/shared';
import { adminPath, portalPath, useApiContext, useWorkflows } from '@servicetim/ui/api';
import type { OptionLoader } from '@servicetim/ui/components';
import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

/*
 * Hooks the agent surfaces need that the shared `src/api` layer does not offer yet (G1's folder
 * is read-only for G4). The integrator moves the reusable ones into `src/api/*` later.
 */

/* ----------------------------- people lookup (agent-safe) ----------------------------- */

/** Row of `GET /v1/users?q=`: members see id/name/avatar, tenant admins also the email. */
export interface UserLookupItem {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  kind: string;
  email?: string;
}

interface UserLookupResponse {
  items: UserLookupItem[];
}

const USER_LOOKUP_LIMIT = 20;
const USER_DIRECTORY_LIMIT = 50;
const usersPath = portalPath('/users');

/**
 * `GET /v1/users?q=&limit=` as an `OptionLoader` for the async pickers (assignee, watchers,
 * mentions, `user` fields). Works for every member; the admin-only users list does not.
 */
export function useAgentUserOptionLoader(): OptionLoader {
  const { request } = useApiContext();
  return useCallback<OptionLoader>(
    async (query, signal) => {
      const q = query.trim();
      const result = await request<UserLookupResponse>('GET', usersPath, {
        query: { q: q.length > 0 ? q : undefined, limit: USER_LOOKUP_LIMIT },
        signal,
      });
      return result.items.map((u) => ({
        id: u.id,
        label: u.displayName,
        description: u.email ?? null,
      }));
    },
    [request],
  );
}

/**
 * The first 50 active people of the tenant (one request, cached a minute): a label cache for
 * ids the API does not resolve into `display` maps (comment authors, history actors).
 * TODO(API): return `authorDisplayName` / actor labels in those DTOs, or accept `?ids=`.
 */
export function useUserDirectory(enabled = true) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: [...keys.users.all, 'directory', USER_DIRECTORY_LIMIT],
    queryFn: ({ signal }) =>
      request<UserLookupResponse>('GET', usersPath, {
        query: { limit: USER_DIRECTORY_LIMIT },
        signal,
      }),
    select: (r) => new Map(r.items.map((u) => [u.id, u.displayName])),
    staleTime: 60_000,
    enabled,
  });
}

/* ----------------------------- forms visible to project admins ----------------------------- */

/**
 * `GET /admin/v1/projects/{id}/forms?kind=` is `project.admin` only, so the query runs only when
 * the caller can administer the project; agents fall back to the bindings (see form-source.ts).
 * TODO(API): an agent-readable "resolved create/edit form for this issue type" endpoint would
 * remove the fallback.
 */
export function useProjectFormsIfAllowed(
  projectId: string | undefined,
  kind: FormKind,
  enabled: boolean,
) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: [...keys.project(projectId ?? '').forms({ kind }), 'agent'],
    queryFn: ({ signal }) =>
      request<Form[]>('GET', adminPath(`/projects/${projectId}/forms`), {
        query: { kind },
        signal,
      }),
    enabled: projectId !== undefined && enabled,
    staleTime: 60_000,
  });
}

/** Options of the select fields among `bindings` (members may read them). */
export function useSelectOptions(bindings: readonly ProjectFieldBinding[] | undefined) {
  const { request, keys } = useApiContext();
  const selectBindings = useMemo(
    () =>
      (bindings ?? []).filter(
        (b) => b.fieldType === 'single_select' || b.fieldType === 'multi_select',
      ),
    [bindings],
  );
  return useQueries({
    queries: selectBindings.map((b) => ({
      queryKey: keys.fields.options(b.fieldId),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        request<FieldOption[]>('GET', adminPath(`/fields/${b.fieldId}/options`), { signal }),
      staleTime: 60_000,
    })),
    combine: (results) => {
      const optionsByFieldId: Record<string, FieldOption[]> = {};
      results.forEach((r, i) => {
        const b = selectBindings[i];
        if (b && r.data) optionsByFieldId[b.fieldId] = r.data;
      });
      return { optionsByFieldId, isPending: results.some((r) => r.isPending) };
    },
  });
}

/* ----------------------------- statuses of a project ----------------------------- */

export interface ProjectStatus extends Status {
  workflowId: string;
}

export interface ProjectStatuses {
  statuses: ProjectStatus[];
  byId: Map<string, ProjectStatus>;
  isPending: boolean;
}

const EMPTY_STATUSES: ProjectStatuses = { statuses: [], byId: new Map(), isPending: false };

/**
 * Every status of the project's published workflows (name, colour, category), from the
 * published `workflow_version` snapshots. Members may read them; used for badges, board
 * columns and the admin "set status" menu.
 */
export function useProjectStatuses(projectId: string | undefined): ProjectStatuses {
  const { request, keys } = useApiContext();
  const workflows = useWorkflows(projectId);
  const published = useMemo(
    () => (workflows.data ?? []).filter((w) => w.publishedVersion !== null),
    [workflows.data],
  );
  const versions = useQueries({
    queries: published.map((w) => ({
      queryKey: keys.project(projectId ?? '').workflowVersion(w.id, w.publishedVersion ?? 0),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        request<WorkflowVersion>(
          'GET',
          adminPath(`/projects/${projectId}/workflows/${w.id}/versions/${w.publishedVersion}`),
          { signal },
        ),
      staleTime: 5 * 60_000,
    })),
    combine: (results) => ({
      data: results.map((r) => r.data),
      isPending: results.some((r) => r.isPending),
    }),
  });
  return useMemo(() => {
    if (projectId === undefined) return EMPTY_STATUSES;
    const statuses: ProjectStatus[] = [];
    for (const version of versions.data) {
      if (!version) continue;
      for (const s of version.snapshot.statuses) {
        statuses.push({ ...s, workflowId: version.workflowId });
      }
    }
    return {
      statuses,
      byId: new Map(statuses.map((s) => [s.id, s])),
      isPending: workflows.isPending || versions.isPending,
    };
  }, [projectId, versions, workflows.isPending]);
}

/* ----------------------------- canned responses ----------------------------- */

/**
 * `GET /admin/v1/projects/{id}/canned-responses` needs `project.admin` today; the composer
 * shows the picker only when the list loads. TODO(API): agent-readable canned list.
 */
export function useProjectCannedResponses(projectId: string | undefined, enabled = true) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: [...keys.project(projectId ?? '').cannedResponses({ pageSize: 200 }), 'composer'],
    queryFn: ({ signal }) =>
      request<Page<CannedResponse>>('GET', adminPath(`/projects/${projectId}/canned-responses`), {
        query: { pageSize: 200 },
        signal,
      }),
    select: (page) => page.items,
    enabled: projectId !== undefined && enabled,
    staleTime: 60_000,
    retry: false,
  });
}

/* ----------------------------- board column paging ----------------------------- */

/**
 * Extra pages of one board column: the grouped response already holds the first page, so this
 * infinite query starts from the group's `nextCursor` and only fetches on demand.
 */
export function useColumnPages(
  request: SearchRequestInput | undefined,
  initialCursor: string | null,
) {
  const { request: send, keys } = useApiContext();
  const query = useInfiniteQuery({
    queryKey: [...keys.search.results(request), 'column', initialCursor],
    queryFn: ({ pageParam, signal }) =>
      send<SearchResponse>('POST', portalPath('/tickets/search'), {
        body: { ...request, page: { ...request?.page, cursor: pageParam } },
        signal,
      }),
    initialPageParam: initialCursor ?? undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: false,
  });
  const items = useMemo<TicketListItem[]>(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );
  const loaded = query.data !== undefined;
  return {
    items,
    hasMore: loaded ? query.hasNextPage : initialCursor !== null,
    isLoadingMore: query.isFetching,
    error: query.error,
    loadMore: () => {
      if (request === undefined || query.isFetching) return;
      void query.fetchNextPage();
    },
  };
}
